import { useEffect, useState } from 'react';
import API from '@/lib/api';
import { mergeFeatures, rolMagFeature } from '@/lib/features';
import { useAuth } from '@/contexts/AuthContext';

// Simpele module-level cache zodat niet elke component de instellingen opnieuw ophaalt.
let _cache = null;
let _pending = null;
const _listeners = new Set();

function loadFeatures() {
  if (_pending) return _pending;
  _pending = API.get('/instellingen').then(r => {
    _cache = mergeFeatures(r.data?.features || {}, r.data || {});
    _listeners.forEach(fn => fn(_cache));
    return _cache;
  }).catch(() => {
    _cache = mergeFeatures({});
    _listeners.forEach(fn => fn(_cache));
    return _cache;
  }).finally(() => { _pending = null; });
  return _pending;
}

export function invalidateFeatureCache() {
  _cache = null;
  loadFeatures();
}

/**
 * useFeature(key) -> boolean — true als de feature actief is EN de user-rol voldoende.
 * Gebruik: const toonStripe = useFeature('int_stripe');
 */
export function useFeature(key) {
  const { user } = useAuth();
  const [cfg, setCfg] = useState(_cache);

  useEffect(() => {
    if (!_cache) loadFeatures();
    const listener = (c) => setCfg(c);
    _listeners.add(listener);
    return () => _listeners.delete(listener);
  }, []);

  if (!cfg) return true; // default: tonen tot configuratie geladen is (voorkomt flicker)
  const f = cfg[key];
  if (!f) return true; // onbekende key → niet blokkeren
  if (!f.actief) return false;
  return rolMagFeature(user?.role || 'user', f.min_rol);
}

/**
 * useAllFeatures() -> {key: bool} voor bulk-gebruik (bv. in sidebar).
 */
export function useAllFeatures() {
  const { user } = useAuth();
  const [cfg, setCfg] = useState(_cache);

  useEffect(() => {
    if (!_cache) loadFeatures();
    const listener = (c) => setCfg(c);
    _listeners.add(listener);
    return () => _listeners.delete(listener);
  }, []);

  if (!cfg) return null;
  const rol = user?.role || 'user';
  const out = {};
  for (const [k, f] of Object.entries(cfg)) {
    out[k] = !!f.actief && rolMagFeature(rol, f.min_rol);
  }
  return out;
}
